import {
    getSharedLogger,
    type AutoPopulatedEventFields,
    type PerformanceEvent as LogPerformanceEvent,
} from '@fiji/common/src/services/logger';

/**
 * Fields the caller supplies for a `category: 'performance'` event —
 * every field the {@link Logger} auto-populates is stripped so the shape
 * matches `PerformanceEvent`'s excess-property check.
 */
type PerformanceEventInput = Omit<LogPerformanceEvent, AutoPopulatedEventFields>;

/**
 * Best-effort resolution of the current page path for `pageName`. Kept
 * synchronous and side-effect-free so it can run from any observer
 * callback, including ones that fire before React has mounted.
 */
const resolvePageName = (): string | undefined =>
    typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : undefined;

/**
 * Namespaced flag hung off `globalThis` so it survives Webpack HMR module
 * re-execution. A plain module-scoped `let` would reset to `false` on every
 * hot reload — the browser retains the original `PerformanceObserver`
 * instances and we would end up with duplicate emission per metric. Same
 * pattern the global error handlers use.
 */
const INSTALLED_KEY = Symbol.for('@fiji/web/perfObserverInstalled');
type GlobalWithFlag = typeof globalThis & { [INSTALLED_KEY]?: boolean };

/**
 * Send one {@link LogPerformanceEvent} through the shared logger, or no-op
 * when the platform logger has not been bootstrapped yet. Metric value is
 * rounded to the nearest millisecond so downstream dashboards keep a
 * stable numeric shape (browsers report sub-millisecond precision).
 */
const emitMetric = (metricName: string, valueMs: number): void => {
    const l = getSharedLogger();
    if (!l) {
        return;
    }
    // Typed explicitly (not as the wider `LogEventInput` union) so
    // TypeScript excess-property checks the shape against `PerformanceEvent`
    // rather than picking the first member of the discriminated union.
    const event: PerformanceEventInput = {
        category: 'performance',
        eventType: 'web_vital',
        metricName,
        valueMs: Math.round(valueMs),
        pageName: resolvePageName(),
    };
    l.info(event);
};

/**
 * Subscribe to a single `PerformanceObserver` entry type and forward each
 * entry to `handler`. Wrapped in a try/catch because Safari and older
 * browsers throw `TypeError` when asked to observe an unknown entry type
 * or an unsupported option (e.g. `durationThreshold`) — we treat that as
 * "metric unavailable" and move on so a missing metric never breaks
 * page load. Returns the created observer so the caller can `disconnect()`
 * once the metric is finalized, or `undefined` when the browser cannot
 * observe the given type.
 *
 * `buffered: true` replays entries that were recorded before this observer
 * was attached, which matters for FCP / LCP since they can fire earlier
 * than our bootstrap.
 */
type ExtendedObserveOptions = PerformanceObserverInit & { durationThreshold?: number };

const observeEntry = (
    entryType: string,
    handler: (entry: PerformanceEntry) => void,
    extraOptions: { durationThreshold?: number } = {}
): PerformanceObserver | undefined => {
    if (typeof PerformanceObserver === 'undefined') {
        return undefined;
    }
    try {
        const observer = new PerformanceObserver((list: PerformanceObserverEntryList): void => {
            list.getEntries().forEach(handler);
        });
        const options: ExtendedObserveOptions = { type: entryType, buffered: true, ...extraOptions };
        observer.observe(options);
        return observer;
    } catch {
        // Unsupported entry type or option — safely skip. Never rethrow: the
        // observer is observation-only and must not affect page rendering.
        return undefined;
    }
};

/**
 * Track the largest INP observed so far this navigation so we only emit
 * on new worsts. The observer is created with `durationThreshold: 40`
 * so the browser filters short interactions before our callback runs —
 * matching the INP definition (40 ms is the same floor Chrome DevTools
 * uses) and avoiding per-keystroke callback overhead.
 */
let worstInpMs = 0;

/**
 * Latest LCP value observed. LCP is emitted once per navigation — at the
 * first user interaction OR when the tab becomes hidden — per the Web
 * Vitals spec. Emitting per update inflates log volume and produces
 * misleading late-navigation values (e.g. a chart hydrating after 30s
 * would report a 30s LCP).
 */
let latestLcpMs = 0;
let lcpObserver: PerformanceObserver | undefined;
let lcpFinalized = false;

/**
 * Emit the final LCP value and disconnect the observer. Idempotent —
 * callable from multiple listeners (`visibilitychange`, first interaction)
 * without duplicating events.
 */
const finalizeLcp = (): void => {
    if (lcpFinalized) {
        return;
    }
    lcpFinalized = true;
    if (latestLcpMs > 0) {
        emitMetric('LCP', latestLcpMs);
    }
    lcpObserver?.disconnect();
    lcpObserver = undefined;
};

/**
 * Install `PerformanceObserver`s for the front-end perf MUSTs from the
 * Confluence logging spec:
 * - `FCP` — First Contentful Paint (perceived load)
 * - `LCP` — Largest Contentful Paint (Core Web Vital)
 * - `INP` — Interaction to Next Paint (responsiveness)
 *
 * Called once from `packages/web/src/index.tsx` before React mounts so
 * early entries are captured via `buffered: true`.
 *
 * Costs nothing on runtimes without `PerformanceObserver` or when the
 * shared logger has not been bootstrapped — every path early-returns.
 */
export const installPerformanceObserver = (): void => {
    if (typeof window === 'undefined') {
        return;
    }
    const g = globalThis as GlobalWithFlag;
    if (g[INSTALLED_KEY]) {
        return;
    }
    g[INSTALLED_KEY] = true;

    // FCP is exposed as one of the entries under the `paint` type; ignore
    // `first-paint` because it is less meaningful (empty pixels can trigger it).
    observeEntry('paint', (entry: PerformanceEntry): void => {
        if (entry.name === 'first-contentful-paint') {
            emitMetric('FCP', entry.startTime);
        }
    });

    // LCP fires whenever a larger element paints. Per the Web Vitals spec
    // we only care about the LAST value before user interaction / tab hide,
    // so track updates silently here and defer emission to `finalizeLcp`.
    lcpObserver = observeEntry('largest-contentful-paint', (entry: PerformanceEntry): void => {
        latestLcpMs = entry.startTime;
    });

    // Finalize LCP on the first user interaction OR when the tab is hidden.
    // `once: true` auto-removes the click/keydown listeners so a second
    // interaction cannot re-fire the emission. `capture: true` guarantees we
    // observe the interaction even if a nested handler stops propagation.
    // Both handlers call the idempotent `finalizeLcp`, so a fast user who
    // interacts before backgrounding the tab still triggers exactly one emit.
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', (): void => {
            if (document.visibilityState === 'hidden') {
                finalizeLcp();
            }
        });
    }
    (['click', 'keydown'] as const).forEach((type): void => {
        window.addEventListener(type, finalizeLcp, { once: true, capture: true });
    });

    // INP: emit only when the current interaction beats the previous worst.
    // `durationThreshold: 40` asks the browser to skip entries below 40 ms
    // (matches Chrome's INP floor) so our callback fires far less often on
    // interaction-heavy sessions. Reading `.duration` off `PerformanceEventTiming`
    // (subtype of PerformanceEntry) is safe because the `'event'` entry type
    // guarantees that shape.
    observeEntry(
        'event',
        (entry: PerformanceEntry): void => {
            const duration = (entry as PerformanceEventTiming).duration;
            if (duration > worstInpMs) {
                worstInpMs = duration;
                emitMetric('INP', duration);
            }
        },
        { durationThreshold: 40 }
    );
};
